'use client';

import {
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
  useState,
} from 'react';

import { cn } from '../lib/cn.ts';

/** The hint, and the id the control's `aria-describedby` already points at. */
export interface FieldNote {
  text: string;
  id: string;
}

/**
 * Draws the hint somewhere other than under the control — beside the label, behind an "i", say.
 * Whatever it renders must carry `note.id` on the element holding the text, so the control keeps
 * the description it had when the hint was printed under it.
 */
export type RenderHint = (note: FieldNote) => ReactNode;

interface FieldShellProps {
  label: string;
  hint?: string;
  renderHint?: RenderHint;
  error?: string;
  optionalLabel?: string;
  required?: boolean;
  /**
   * A small mark beside the label. Decorative and hidden from assistive technology: it repeats
   * what the label already says, and a screen reader announcing "person, full name" is worse than
   * one announcing "full name".
   */
  icon?: ReactNode;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
}

/**
 * Label, control, hint and error as one unit.
 *
 * The wiring is the point. A label needs `htmlFor`, a hint and an error need to reach the
 * control through `aria-describedby`, and an invalid control needs `aria-invalid` — get any of
 * them wrong and a screen-reader user is told there is a field but not what it wants or why it
 * was rejected. Generating the ids here means no call site can forget.
 */
function FieldShell({
  label,
  hint,
  renderHint,
  error,
  optionalLabel,
  required,
  icon,
  children,
  className,
}: FieldShellProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  // Both, when both are present — a control can be described by more than one element.
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  // Beside the label rather than inside it: a `<label>` names its control from everything in it,
  // and a button in there would add its own name to the field's.
  const aside = hint && hintId && renderHint ? renderHint({ text: hint, id: hintId }) : null;

  const labelElement = (
    <label className="awj-field__label" htmlFor={id}>
      {icon ? (
        <span className="awj-field__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {label}
      {/*
        The optional marker rather than a required one: on a form where most fields are
        required, marking the exceptions is less noise than marking the rule.
      */}
      {!required && optionalLabel ? (
        <span className="awj-field__optional">({optionalLabel})</span>
      ) : null}
    </label>
  );

  return (
    <div className={cn('awj-field', className)}>
      {aside ? (
        <span className="awj-field__head">
          {labelElement}
          {aside}
        </span>
      ) : (
        labelElement
      )}

      {children({ id, describedBy, invalid: Boolean(error) })}

      {hint && !aside ? (
        <span className="awj-field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}

      {error ? (
        // Announced when it appears, without moving focus away from what the user is typing.
        <span className="awj-field__error" id={errorId} role="alert">
          <ErrorIcon />
          {error}
        </span>
      ) : null}
    </div>
  );
}

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'> {
  label: string;
  hint?: string;
  /** Draws the hint beside the label instead of under the control — see `RenderHint`. */
  renderHint?: RenderHint;
  error?: string;
  optionalLabel?: string;
  /** A small decorative mark beside the label — see `FieldShell`. */
  icon?: ReactNode;
  className?: string;
}

export function TextField({
  label,
  hint,
  renderHint,
  error,
  optionalLabel,
  icon,
  className,
  ...rest
}: TextFieldProps) {
  return (
    <FieldShell
      label={label}
      hint={hint}
      renderHint={renderHint}
      error={error}
      optionalLabel={optionalLabel}
      required={rest.required}
      icon={icon}
      className={className}
    >
      {({ id, describedBy, invalid }) => (
        <input
          className="awj-input"
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

export interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'className'> {
  label: string;
  hint?: string;
  /** Draws the hint beside the label instead of under the control — see `RenderHint`. */
  renderHint?: RenderHint;
  error?: string;
  optionalLabel?: string;
  /** A small decorative mark beside the label — see `FieldShell`. */
  icon?: ReactNode;
  className?: string;
}

export function TextAreaField({
  label,
  hint,
  renderHint,
  error,
  optionalLabel,
  icon,
  className,
  ...rest
}: TextAreaFieldProps) {
  return (
    <FieldShell
      label={label}
      hint={hint}
      renderHint={renderHint}
      error={error}
      optionalLabel={optionalLabel}
      required={rest.required}
      icon={icon}
      className={className}
    >
      {({ id, describedBy, invalid }) => (
        <textarea
          className="awj-textarea"
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'className' | 'children'> {
  label: string;
  options: SelectOption[];
  placeholder?: string;
  hint?: string;
  /** Draws the hint beside the label instead of under the control — see `RenderHint`. */
  renderHint?: RenderHint;
  error?: string;
  optionalLabel?: string;
  className?: string;
}

export function SelectField({
  label,
  options,
  placeholder,
  hint,
  renderHint,
  error,
  optionalLabel,
  className,
  ...rest
}: SelectFieldProps) {
  return (
    <FieldShell
      label={label}
      hint={hint}
      renderHint={renderHint}
      error={error}
      optionalLabel={optionalLabel}
      required={rest.required}
      className={className}
    >
      {({ id, describedBy, invalid }) => (
        <span className="awj-select-wrapper">
          <select
            className="awj-select"
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            {...rest}
          >
            {placeholder ? (
              // Disabled, so it cannot be chosen back once a real option has been picked.
              <option value="" disabled>
                {placeholder}
              </option>
            ) : null}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="awj-select-wrapper__chevron" aria-hidden="true">
            <ChevronIcon />
          </span>
        </span>
      )}
    </FieldShell>
  );
}

export interface PasswordFieldProps extends Omit<TextFieldProps, 'type' | 'icon'> {
  /** The toggle's name while the password is hidden — "Show password", in the reader's language. */
  showLabel: string;
  /** Its name while the password is visible. */
  hideLabel: string;
}

/**
 * A password field with a way to see what was typed.
 *
 * The same shell as every other field, so it lines up with the email above it; the only addition
 * is the eye at the field's reading end. A toggle, not a hold-to-peek — a press that has to be held
 * is out of reach of anyone who cannot hold one — and its name says what it will do next. The back
 * office draws the same control from the same class names.
 */
export function PasswordField({
  label,
  hint,
  renderHint,
  error,
  optionalLabel,
  className,
  showLabel,
  hideLabel,
  ...rest
}: PasswordFieldProps) {
  const [shown, setShown] = useState(false);

  return (
    <FieldShell
      label={label}
      hint={hint}
      renderHint={renderHint}
      error={error}
      optionalLabel={optionalLabel}
      required={rest.required}
      className={className}
    >
      {({ id, describedBy, invalid }) => (
        <span className="field-reveal">
          <input
            className="awj-input field-reveal__input"
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            {...rest}
            type={shown ? 'text' : 'password'}
          />
          <button
            type="button"
            className="field-reveal__toggle"
            aria-label={shown ? hideLabel : showLabel}
            aria-pressed={shown}
            aria-controls={id}
            onClick={() => setShown((current) => !current)}
          >
            <EyeIcon crossed={shown} />
          </button>
        </span>
      )}
    </FieldShell>
  );
}

function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {crossed ? (
        <>
          <path d="M9.9 6a9.6 9.6 0 012.1-.4c5.8 0 9.2 6.4 9.2 6.4a16 16 0 01-2.4 3.2M6.2 7.6A15.4 15.4 0 002.8 12s3.4 6.4 9.2 6.4a9 9 0 004.4-1.1" />
          <path d="M10 10a2.8 2.8 0 004 4M4.6 4.6l14.8 14.8" />
        </>
      ) : (
        <>
          <path d="M2.8 12s3.4-6.4 9.2-6.4 9.2 6.4 9.2 6.4-3.4 6.4-9.2 6.4S2.8 12 2.8 12z" />
          <circle cx="12" cy="12" r="2.8" />
        </>
      )}
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 4.5v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="11.2" r="0.9" fill="currentColor" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="m4 6 4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
